import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import { importFileHandler } from "../utils/NetworkHanlder";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const ImportResultDetails = ({ data }) => {
  const { deviceCalibrationsImportResult, importCalibrationResult } = data;

  const detailLevels = [
    { label: "Dodano:", value: deviceCalibrationsImportResult.created },
    { label: "Usunięto:", value: deviceCalibrationsImportResult.deleted },
  ];

  const inclusionLevels = [
    { key: "BY_BASE", label: "Wg bazy:" },
    { key: "BY_MANUFACTURER", label: "Wg producenta:" },
    { key: "BY_BRAND", label: "Wg marki:" },
    { key: "BY_MODEL", label: "Wg modelu:" },
  ];

  const getCountString = (levelKey) => {
    const item = importCalibrationResult.calibrationInclusionCountByAveragingLevel.find(
      (i) => i.averagingLevel === levelKey
    );
    return item
      ? `Włączono: ${item.inclusionCount}, Wyłączono: ${item.exclusionCount}`
      : "brak danych";
  };

  return (
    <div>
      <Typography
        variant="h6"
        component="div"
        style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "12px" }}
      >
        Import zakończony pomyślnie!
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns="auto 1fr"
        style={{ gap: "3px 17px" }}
      >
        <Typography
          variant="body1"
          style={{ gridColumn: "span 2", marginBottom: "4px" }}
        >
          <strong>Wynik importu kalibracji</strong>
        </Typography>

        {detailLevels.map((item) => (
          <React.Fragment key={item.label}>
            <Typography variant="body2">
              <strong>{item.label}</strong>
            </Typography>
            <Typography variant="body2">{item.value}</Typography>
          </React.Fragment>
        ))}

        <Box gridColumn="span 2" height="8px" />

        <Typography
          variant="body1"
          style={{ gridColumn: "span 2", marginBottom: "4px" }}
        >
          <strong>Modyfikacje uśrednionych kalibracji</strong>
        </Typography>

        {inclusionLevels.map((level) => (
          <React.Fragment key={level.key}>
            <Typography variant="body2" style={{ fontStyle: "italic" }}>
              <strong>{level.label}</strong>
            </Typography>
            <Typography variant="body2">{getCountString(level.key)}</Typography>
          </React.Fragment>
        ))}
      </Box>
    </div>
  );
};

export default function ImportDialog({
  setModalOpen,
  modalOpen,
  importOptions,
  setImportOptions,
  fileData,
  setFileData,
  updateData,
  filters,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: null,
    severity: "success",
  });

  const handleConfirmImport = async () => {
    if (!fileData) {
      setNotification({
        open: true,
        message: "Proszę najpierw wybrać plik!",
        severity: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await importFileHandler(fileData, importOptions);
      if (updateData) updateData(filters);

      setNotification({
        open: true,
        message: <ImportResultDetails data={data} />,
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: "Wystąpił błąd podczas importu. Sprawdź konsolę.",
        severity: "error",
      });
      console.error("Błąd importu:", error);
    } finally {
      setIsLoading(false);
      setModalOpen(false);
      if (setFileData) setFileData(null);
    }
  };

  const handleReject = async () => {
    setNotification({
      open: false,
      ...notification,
    });
    setIsLoading(false);
    setModalOpen(false);
    if (setFileData) setFileData(null);
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === "clickaway") return;
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Dialog
        open={modalOpen}
        onClose={() => !isLoading && setModalOpen(false)}
        aria-labelledby="import-dialog-title"
      >
        <DialogTitle id="import-dialog-title">Potwierdź import</DialogTitle>
        <DialogContent>
          <Typography>
            Plik został wczytany. Wybierz opcje importu przed ostatecznym
            zatwierdzeniem.
          </Typography>
          <Box mt={2} display="flex" flexDirection="column">
            <FormControlLabel
              control={
                <Switch
                  checked={importOptions.keepDeviceCalibrationsDoneAfterExport}
                  onChange={(e) =>
                    setImportOptions({
                      ...importOptions,
                      keepDeviceCalibrationsDoneAfterExport: e.target.checked,
                    })
                  }
                  name="keepDeviceCalibrationsDoneAfterExport"
                  color="primary"
                />
              }
              label="Zachowaj kalibracje wykonane po eksporcie"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={
                    importOptions.revertDeviceCalibrationsDeletedAfterExport
                  }
                  onChange={(e) =>
                    setImportOptions({
                      ...importOptions,
                      revertDeviceCalibrationsDeletedAfterExport:
                        e.target.checked,
                    })
                  }
                  name="revertDeviceCalibrationsDeletedAfterExport"
                  color="primary"
                />
              }
              label="Przywróć kalibracje usunięte po eksporcie"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleReject()}
            color="secondary"
            disabled={isLoading}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleConfirmImport}
            color="primary"
            variant="contained"
            disabled={isLoading}
            style={{ minWidth: "150px" }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Akceptuj i importuj"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={10000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
